import { type ViolationEventResponse } from '@sitewise/shared';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  ErrorState,
  SeverityBadge,
  Skeleton,
  cn,
} from '@sitewise/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, ExternalLink, RefreshCcw } from 'lucide-react';
import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import {
  acknowledgeViolation,
  createViolationWorkOrder,
  dismissViolation,
  fetchLinkableWorkOrders,
  fetchViolation,
  linkViolationWorkOrder,
  refreshViolation,
  reopenViolation,
  unlinkViolationWorkOrder,
} from '../../../api/violations.js';

import {
  agencyLabel,
  formatDate,
  formatDateTime,
  formatMoney,
  formatRelativeDays,
  severityLabel,
  statusLabel,
} from './lib.js';

export function ViolationDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [linkOpen, setLinkOpen] = useState(false);
  const [dismissReason, setDismissReason] = useState('');

  const detailQuery = useQuery({
    queryKey: ['violations', 'detail', id],
    queryFn: () => fetchViolation(id!),
    enabled: !!id,
  });

  const ackMutation = useMutation({
    mutationFn: () => acknowledgeViolation(id!),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['violations'] }),
  });
  const dismissMutation = useMutation({
    mutationFn: () => dismissViolation(id!, { reason: dismissReason }),
    onSuccess: () => {
      setDismissReason('');
      queryClient.invalidateQueries({ queryKey: ['violations'] });
    },
  });
  const unlinkMutation = useMutation({
    mutationFn: () => unlinkViolationWorkOrder(id!),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['violations'] }),
  });
  const createWoMutation = useMutation({
    mutationFn: () => createViolationWorkOrder(id!),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['violations'] });
      navigate(`/work-orders/${result.workOrderId}`);
    },
  });
  const refreshMutation = useMutation({
    mutationFn: () => refreshViolation(id!),
  });
  const reopenMutation = useMutation({
    mutationFn: () => reopenViolation(id!),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['violations'] }),
  });

  if (!id) return null;
  if (detailQuery.isError) {
    return <ErrorState description="Could not load this violation." />;
  }
  if (detailQuery.isLoading || !detailQuery.data) {
    return (
      <div className="space-y-3 p-4">
        <Skeleton className="h-8 w-1/2" />
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-48 w-full" />
      </div>
    );
  }

  const { violation, events } = detailQuery.data;
  const cureOverdue =
    violation.cureByAt != null && new Date(violation.cureByAt).getTime() < Date.now();

  return (
    <div className="mx-auto max-w-4xl space-y-4 p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <Button variant="ghost" size="sm" asChild>
          <Link to="/violations">
            <ArrowLeft className="h-4 w-4" />
            All violations
          </Link>
        </Button>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            disabled={refreshMutation.isPending}
            onClick={() => refreshMutation.mutate()}
          >
            <RefreshCcw className="h-4 w-4" />
            Refresh now
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader className="flex flex-col gap-1.5">
          <div className="flex flex-wrap items-center gap-2">
            <SeverityBadge level={violation.severity}>{severityLabel(violation.severity)}</SeverityBadge>
            <span className="text-xs uppercase tracking-wide text-fg-muted">
              {agencyLabel(violation.agency)} · {violation.kind}
            </span>
            <span className="text-xs text-fg-muted">{statusLabel(violation.status)}</span>
            {violation.acknowledgedAt ? (
              <span className="text-xs text-fg-muted">
                Acknowledged {formatRelativeDays(violation.acknowledgedAt)}
              </span>
            ) : null}
          </div>
          <h1 className="text-lg font-semibold">{violation.summary}</h1>
          {violation.buildingName ? (
            <p className="text-sm text-fg-muted">
              Building:{' '}
              <Link
                to={`/buildings/${violation.buildingId}`}
                className="text-primary underline-offset-2 hover:underline"
              >
                {violation.buildingName}
              </Link>
              {violation.unitNumber ? ` · Unit ${violation.unitNumber}` : ''}
            </p>
          ) : null}
        </CardHeader>
        <CardContent className="grid grid-cols-1 gap-4 md:grid-cols-3">
          <Field label="Issued">
            <div>{formatDate(violation.issuedAt)}</div>
            <div className="text-xs text-fg-muted">{formatRelativeDays(violation.issuedAt)}</div>
          </Field>
          <Field label="Cure-by">
            <div className={cn(cureOverdue ? 'text-danger' : '')}>
              {formatDate(violation.cureByAt)}
            </div>
            <div className={cn('text-xs', cureOverdue ? 'text-danger' : 'text-fg-muted')}>
              {formatRelativeDays(violation.cureByAt)}
            </div>
          </Field>
          <Field label="Penalty">
            <div className="tabular-nums">
              {formatMoney(violation.monetaryPenalty, violation.currency)}
            </div>
          </Field>
          <Field label="External id">
            <span className="font-mono text-xs">{violation.externalId}</span>
          </Field>
          <Field label="First observed">
            <div className="text-xs">{formatDateTime(violation.firstSeenAt)}</div>
          </Field>
          <Field label="Last seen">
            <div className="text-xs">{formatDateTime(violation.lastSeenAt)}</div>
          </Field>
          {violation.locationText ? (
            <Field label="Location">{violation.locationText}</Field>
          ) : null}
          {violation.externalUrl ? (
            <Field label="Source link">
              <a
                href={violation.externalUrl}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1 text-primary underline-offset-2 hover:underline"
              >
                Open agency record <ExternalLink className="h-3 w-3" />
              </a>
            </Field>
          ) : null}
          {violation.summaryAi ? (
            <Field label="AI summary" className="md:col-span-3">
              <div className="rounded border border-primary/40 bg-primary-subtle/40 px-3 py-2 text-sm leading-relaxed">
                <span className="text-xs uppercase tracking-wide text-primary">
                  Plain-English gloss
                </span>
                <p className="mt-1 whitespace-pre-line">{violation.summaryAi}</p>
              </div>
            </Field>
          ) : null}
          {violation.description ? (
            <Field label="Description" className="md:col-span-3">
              <p className="whitespace-pre-line text-sm leading-relaxed">{violation.description}</p>
            </Field>
          ) : null}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <h2 className="text-sm font-semibold">Actions</h2>
        </CardHeader>
        <CardContent className="space-y-3">
          {violation.status === 'unknown' ? (
            <div className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-warning/40 bg-warning-subtle/40 px-3 py-2">
              <p className="text-sm">
                We couldn&rsquo;t map this agency status automatically. If it&rsquo;s a real,
                active violation, mark it open.
              </p>
              <Button
                size="sm"
                onClick={() => reopenMutation.mutate()}
                disabled={reopenMutation.isPending}
              >
                Mark as open
              </Button>
            </div>
          ) : null}
          <div className="flex flex-wrap items-center gap-2">
            {!violation.acknowledgedAt ? (
              <Button
                size="sm"
                variant="outline"
                onClick={() => ackMutation.mutate()}
                disabled={ackMutation.isPending}
              >
                Acknowledge
              </Button>
            ) : null}
            {violation.autoWorkOrderId ? (
              <>
                <Button size="sm" variant="outline" asChild>
                  <Link to={`/work-orders/${violation.autoWorkOrderId}`}>Open WO</Link>
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => unlinkMutation.mutate()}
                  disabled={unlinkMutation.isPending}
                >
                  Unlink
                </Button>
              </>
            ) : (
              <>
                <Button
                  size="sm"
                  onClick={() => createWoMutation.mutate()}
                  disabled={createWoMutation.isPending}
                >
                  Turn into work order
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setLinkOpen((v) => !v)}
                >
                  Link existing WO
                </Button>
              </>
            )}
            {violation.status !== 'dismissed' ? (
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  placeholder="Dismiss reason…"
                  value={dismissReason}
                  onChange={(e) => setDismissReason(e.target.value)}
                  className="h-9 rounded border border-border bg-surface px-3 text-sm"
                />
                <Button
                  size="sm"
                  variant="ghost"
                  className="text-danger"
                  onClick={() => dismissMutation.mutate()}
                  disabled={!dismissReason.trim() || dismissMutation.isPending}
                >
                  Dismiss
                </Button>
              </div>
            ) : null}
          </div>
          {linkOpen ? <LinkPicker violationId={id} onLinked={() => setLinkOpen(false)} /> : null}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <h2 className="text-sm font-semibold">Activity</h2>
        </CardHeader>
        <CardContent>
          {events.length === 0 ? (
            <p className="text-sm text-fg-muted">No activity yet.</p>
          ) : (
            <ul className="space-y-3">
              {events.map((e) => (
                <ActivityRow key={e.id} event={e} />
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function Field({
  label,
  children,
  className,
}: {
  label: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={cn('space-y-0.5', className)}>
      <div className="text-xs uppercase tracking-wide text-fg-muted">{label}</div>
      <div className="text-sm">{children}</div>
    </div>
  );
}

function ActivityRow({ event }: { event: ViolationEventResponse }) {
  return (
    <li className="rounded-md border border-border bg-surface px-3 py-2">
      <div className="flex items-center justify-between gap-2 text-xs">
        <span className="font-medium uppercase tracking-wide text-fg-muted">
          {event.kind.replace(/_/g, ' ')}
        </span>
        <span className="text-fg-muted">{formatDateTime(event.createdAt)}</span>
      </div>
      {event.notes ? <p className="mt-1 text-sm">{event.notes}</p> : null}
      {event.before || event.after ? (
        <pre className="mt-2 max-h-40 overflow-auto rounded bg-surface-muted p-2 text-xs">
{JSON.stringify({ before: event.before, after: event.after }, null, 2)}
        </pre>
      ) : null}
    </li>
  );
}

function LinkPicker({
  violationId,
  onLinked,
}: {
  violationId: string;
  onLinked: () => void;
}) {
  const queryClient = useQueryClient();
  const woQuery = useQuery({
    queryKey: ['violations', 'linkable', violationId],
    queryFn: () => fetchLinkableWorkOrders(violationId),
  });
  const linkMutation = useMutation({
    mutationFn: (workOrderId: string) =>
      linkViolationWorkOrder(violationId, { workOrderId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['violations'] });
      onLinked();
    },
  });

  if (woQuery.isLoading) return <Skeleton className="h-24 w-full" />;
  if (!woQuery.data || woQuery.data.workOrders.length === 0) {
    return <p className="text-sm text-fg-muted">No open work orders for this building.</p>;
  }

  return (
    <ul className="space-y-1">
      {woQuery.data.workOrders.map((wo) => (
        <li
          key={wo.id}
          className="flex items-center justify-between rounded-md border border-border bg-surface px-3 py-2"
        >
          <div>
            <div className="text-sm font-medium">{wo.displayNumber}</div>
            <div className="text-xs text-fg-muted">{wo.title}</div>
          </div>
          <Button
            size="sm"
            variant="outline"
            disabled={linkMutation.isPending}
            onClick={() => linkMutation.mutate(wo.id)}
          >
            Link
          </Button>
        </li>
      ))}
    </ul>
  );
}
